// Serwis w APP (docs/pwa/SERWIS.md; worker-go/api/serwis.go, serwis_admin.go). Stan poza Reactem: role konta, klienci
// (jako serwisant) i dostępy do konta (jako klient) - wczytywany przy starcie i po zdarzeniu SSE `konto`
// (`serwis.zmiana`); wtedy też pulpity klientów serwisanta (lista sztuk odświeża brama sama).
import { useSyncExternalStore } from "react";
import type { AccountEvent, ClientLookupReply, CzasSerwisu, OKReply, ScreenRequestIn, ServiceReply, ServiceRequestIn,
  ServiceTesterProfileIn, ServiceTesterReply, ServiceWifiIn } from "../gen/kontrakt";
import { wczytajPulpity } from "./pulpity";
import { zapytanie } from "./zaplecze";

let stan: ServiceReply | null = null; // null - jeszcze nie wczytany
const sluchacze = new Set<() => void>();
let wczytywanie: Promise<void> | null = null;
let zegar = 0;

function ustaw(s: ServiceReply | null): void {
  stan = s;
  for (const f of sluchacze) f();
  zaplanujWygasniecie();
}

// Prośba i dostęp na czas wygasają bez zdarzenia - stan od nowa w chwili najbliższego końca („wygasla”,
// „zakonczony” przez czas podaje serwer), żeby baner prośby i ekran Serwis nie wisiały po terminie.
function zaplanujWygasniecie(): void {
  window.clearTimeout(zegar);
  if (!stan) return;
  const konce = [...stan.dostepy, ...stan.klienci].flatMap((g) =>
    g.stan === "prosba" ? [g.prosba_do] : g.stan === "aktywny" && g.do != null ? [g.do] : []).concat(
    stan.ekrany.flatMap((e) => (e.stan === "prosba" ? [e.prosba_do] : e.stan === "aktywna" && e.do != null ? [e.do] : [])));
  if (konce.length === 0) return;
  const za = Math.min(...konce) * 1000 - Date.now() + 2000; // chwila zapasu na zegar serwera
  zegar = window.setTimeout(() => void wczytajSerwis(), Math.min(Math.max(za, 1000), 2 ** 31 - 1));
}

/** Wczytuje stan serwisu (jedno zapytanie naraz); błąd - zostaje poprzedni. */
export function wczytajSerwis(): Promise<void> {
  wczytywanie ??= zapytanie<ServiceReply>("GET", "/api/serwis")
    .then(ustaw, () => undefined)
    .finally(() => { wczytywanie = null; });
  return wczytywanie;
}

/** Zdarzenie SSE `konto`: prośby albo dostępy serwisu konta zmienione. */
export function przyjmijZdarzenieSerwisu(e: AccountEvent): void {
  if (e.rodzaj !== "serwis.zmiana") return;
  void wczytajSerwis();
  void wczytajPulpity();
}

/** Po każdej zmianie - stan od nowa (ten telefon nie czeka na zdarzenie). */
const iOdswiez = async <T,>(p: Promise<T>): Promise<T> => {
  const o = await p;
  await wczytajSerwis();
  return o;
};

/** Prośba serwisanta: e-mail klienta (całe konto) albo numer urządzenia (ta sztuka), powód, proponowany czas. */
export const poprosOdostep = (p: ServiceRequestIn) =>
  iOdswiez(zapytanie<OKReply>("POST", "/api/serwis/prosba", p));
export const zgoda = (id: number, czas: CzasSerwisu) =>
  iOdswiez(zapytanie<OKReply>("POST", `/api/serwis/${id}/zgoda`, { czas }));
export const odrzucProsbe = (id: number) => iOdswiez(zapytanie<OKReply>("POST", `/api/serwis/${id}/odrzuc`));
export const zakonczDostep = (id: number) => iOdswiez(zapytanie<OKReply>("POST", `/api/serwis/${id}/zakoncz`));

// --- wejście do klienta z ekranem (docs/admin/PROJEKT.md 5.2; worker-go/api/serwis_ekran.go): zgoda klienta za każdym
// razem, kanał na żywo - brama/ekran.ts ---

/** Prośba admina albo serwisanta o podgląd ekranu: e-mail klienta albo numer jego urządzenia. */
export const poprosOEkran = (klient: string) =>
  iOdswiez(zapytanie<OKReply>("POST", "/api/serwis/ekran", { klient } satisfies ScreenRequestIn));
export const pokazEkran = (id: number) => iOdswiez(zapytanie<OKReply>("POST", `/api/serwis/ekran/${id}/zgoda`));
export const odrzucEkran = (id: number) => iOdswiez(zapytanie<OKReply>("POST", `/api/serwis/ekran/${id}/odrzuc`));
export const zakonczEkran = (id: number) => iOdswiez(zapytanie<OKReply>("POST", `/api/serwis/ekran/${id}/zakoncz`));

// --- wejście do klienta (admin i serwisant w karcie Serwis APP, Q8; lista serwisantów - panel, /api/admin) ---

/** „Wejdź do klienta”: konto i numery jego sztuk po adresie e-mail albo numerze sztuki. */
export const szukajKlienta = (szukaj: string) =>
  zapytanie<ClientLookupReply>("GET", "/api/serwis/klient?szukaj=" + encodeURIComponent(szukaj));

// --- tester serwisowy (worker-go/api/serwis_tester.go): własne profile nastaw i sieć WiFi dla sterownika przez tester.
// Każda zmiana odpowiada pełnym stanem sekcji - bez drugiego zapytania ---

export const wczytajTester = () => zapytanie<ServiceTesterReply>("GET", "/api/serwis/tester");
export const zapiszProfilTestera = (p: ServiceTesterProfileIn) =>
  zapytanie<ServiceTesterReply>("POST", "/api/serwis/tester/profil", p);
export const usunProfilTestera = (id: number) => zapytanie<ServiceTesterReply>("DELETE", `/api/serwis/tester/profil/${id}`);
/** „Wyślij do testera”: tester od razu pobiera profile (wyłączony - sam po włączeniu). */
export const wyslijProfileDoTestera = (sn: string) =>
  zapytanie<OKReply>("POST", `/api/serwis/tester/${encodeURIComponent(sn)}/profil`);
export const wyslijWifiTesterem = (sn: string, p: ServiceWifiIn) =>
  zapytanie<ServiceTesterReply>("POST", `/api/serwis/tester/${encodeURIComponent(sn)}/wifi`, p);

/** Wylogowanie: bez stanu poprzedniego konta. */
export function wyczyscSerwis(): void {
  ustaw(null);
}

function subskrybuj(f: () => void): () => void {
  sluchacze.add(f);
  return () => sluchacze.delete(f);
}

export function useSerwis(): ServiceReply | null {
  return useSyncExternalStore(subskrybuj, () => stan);
}
